import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useQuery } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AdContext } from '../contexts/ads-context';
import { AppContext } from '../contexts/app-context';
import { useAllianceIdentity } from '../hooks/use-alliance-identity';
import { useSessionActions } from '../hooks/use-session-actions';
import { captureError } from '../utils/capture-error';
import { AllianceAppBar } from './alliance/alliance-app-bar';
import { buildRecruitLink } from './alliance/alliance-apps';
import { AlliancePerson, RecruitRelation } from './alliance/alliance-identity';
import { SIDEBAR_WIDTH } from './alliance/app-nav-drawer';
import { BarBadge, BarButton } from './alliance/bar-controls';
import { CURRENT_APP } from './alliance/current-app';
import { RecruitLink } from './alliance/recruit-link';
import { UserControl } from './alliance/user-control';

/**
 * The notifications bell for the actions slot. The count is the unread texts
 * and actions the notifications drawer last loaded; clicking toggles that
 * drawer, as before.
 */
function NotificationsButton() {
  // Context
  const {
    dispatch,
    state: { notificationsData, notificationsOpen },
  } = React.useContext(AppContext);

  // Unread Texts
  const unreadTexts = notificationsData.texts.filter((i) => !i.MsgRead);
  // Unread Actions
  const unreadActions = notificationsData.actions.filter((i) => i.Read === '0');
  // Unread Notifications
  const unreadNotifications = unreadActions.length + unreadTexts.length;

  return (
    <BarButton
      square
      open={notificationsOpen}
      aria-label={
        unreadNotifications
          ? `Notifications, ${unreadNotifications} unread`
          : 'Notifications'
      }
      onClick={() => dispatch({ type: 'TOGGLE_NOTIFICATIONS_DRAWER' })}
    >
      {unreadNotifications > 0 ? (
        <NotificationsIcon sx={{ fontSize: 21 }} />
      ) : (
        <NotificationsNoneIcon sx={{ fontSize: 21 }} />
      )}
      <BarBadge
        count={unreadNotifications}
        background={CURRENT_APP.badge.bg}
        color={CURRENT_APP.badge.fg}
        title="Unread notifications"
        position={{ top: -7, right: -7 }}
      />
    </BarButton>
  );
}

/**
 * "Recruit an agent" in the user menu. The sign-up link carries the agent's
 * registration GUID from Contracting's `/register/get-uuid`, fetched the
 * first time the section is opened and kept for the session. The query key
 * names the agent, so stopping or switching impersonation drops it.
 *
 * Today the API resolves the GUID from the signed-in user's token (staff get
 * the agent they're staff for). Impersonated cases are hidden by
 * RECRUIT_LINK_WHEN_IMPERSONATING until the API accepts an agent number.
 */
function ShellRecruitLink(props: {
  agent: AlliancePerson;
  relation: RecruitRelation;
}) {
  // State
  const [requested, setRequested] = React.useState(false);

  // Query - Registration GUID
  const pathUuid = '/register/get-uuid';
  const queryUuid = useQuery({
    enabled: requested,
    queryKey: ['ContractingAPI', pathUuid, { AgtNo: props.agent.id }],
    queryFn: async () => {
      // TODO(backend): send the impersonated agent's number once the API
      // supports it (parameter name to be confirmed)
      const response: { data: string } = await API.post(
        'ContractingAPI',
        pathUuid,
        {},
      );
      return response.data;
    },
    staleTime: Infinity,
    onError: (error) => captureError({ data: { error } }),
  });

  const handleOpen = React.useCallback(() => setRequested(true), []);

  return (
    <RecruitLink
      agent={props.agent}
      relation={props.relation}
      link={queryUuid.data ? buildRecruitLink({ uuid: queryUuid.data }) : null}
      loading={!queryUuid.data && !queryUuid.isError}
      error={queryUuid.isError}
      onOpen={handleOpen}
      onRetry={() => queryUuid.refetch()}
    />
  );
}

/**
 * Dashboard's App Bar: the page title (desktop) or the compact lockup and
 * menu button (phones), the notifications bell, the user control (Account,
 * RecruitLink, Stop impersonating, Sign out) and the App Launcher. Rendered
 * once by the shell (components/dashboard.tsx), right of the sidebar and
 * under the ad banner carousel.
 */
export function AppShellBar() {
  // Context
  const { state, dispatch } = React.useContext(AppContext);
  const adContext = React.useContext(AdContext);
  // Hooks
  const navigate = useNavigate();
  const identity = useAllianceIdentity();
  const { signOut, stopImpersonating } = useSessionActions();

  return (
    <AllianceAppBar
      title={state.routeTitle}
      sidebarWidth={SIDEBAR_WIDTH}
      topOffset={adContext.state.bannerHeight}
      loading={state.routeLoadingCount > 0}
      onMenuClick={() => dispatch({ type: 'TOGGLE_DRAWER' })}
      menuOpen={state.drawerOpen}
      actions={<NotificationsButton />}
      user={
        identity ? (
          <UserControl
            identity={identity}
            onSignOut={signOut}
            onStopImpersonating={stopImpersonating}
            actions={[
              {
                label: 'Account',
                icon: <ManageAccountsIcon sx={{ fontSize: 18 }} />,
                onClick: () => navigate('/account'),
              },
            ]}
            renderRecruitLink={(target) => (
              <ShellRecruitLink
                key={target.agent.id}
                agent={target.agent}
                relation={target.relation}
              />
            )}
          />
        ) : undefined
      }
    />
  );
}
